/** ステップ1: プランの選択 (mfk-purchase-subsystem req3-7 / モック意匠) */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { purchaseApi, type Product, type PaymentMethod } from '../api/purchaseApi';
import { loadState, saveState, type CartLine } from '../state/purchaseState';
import { includedOf, yen } from '../state/calc';
import { sortByServiceOrder } from '../serviceOrder';
import { PurchaseLayout } from './PurchaseLayout';

/** 製品コード別の「できること」機能リスト（プランカードに表示）。 */
const PLAN_FEATURES: Record<string, { title: string; items: string[] }> = {
  GB_NAVI: {
    title: 'GBなびでできること',
    items: [
      '週次コンディション把握',
      'モチベーション変化の追跡',
      '不調サインの早期発見',
      '週次・月次レポート',
      '部署・チーム別の定点比較',
    ],
  },
  GB_JINZAI: {
    title: 'GB人財でできること',
    items: [
      '思考タイプ分析',
      '強み・特性の可視化',
      '個人レポート納品',
      '相性分析',
      '採用ミスマッチ防止',
      '適材適所の配置支援',
    ],
  },
  GB_SOSHIKI: {
    title: 'GB組織でできること',
    items: [
      '人財構成の可視化',
      'コミュニケーションバランス分析',
      '働きやすさの診断',
      '組織力スコア',
      '組織レポート納品',
      'チーム編成・組織編成支援',
    ],
  },
};

export const PurchasePlanPage: React.FC = () => {
  const nav = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<Record<string, CartLine>>({});

  useEffect(() => {
    const st = loadState();
    purchaseApi.getProducts().then((r) => {
      // 表示順: GBなび → GB人財 → GB組織（serviceOrder に一元化）
      const sorted = sortByServiceOrder(r.products, (p) => p.code);
      setProducts(sorted);
      const init: Record<string, CartLine> = {};
      sorted.forEach((p) => {
        init[p.code] = st.cart[p.code] || { checked: false, userCount: 1, paymentMethod: 'lump' };
      });
      setCart(init);
    });
  }, []);

  const up = (code: string, patch: Partial<CartLine>) =>
    setCart((c) => ({ ...c, [code]: { ...c[code], ...patch } }));

  const selected = useMemo(
    () => products.filter((p) => cart[p.code]?.checked),
    [products, cart]
  );
  const lumpTotal = selected
    .filter((p) => cart[p.code].paymentMethod === 'lump')
    .reduce((a, p) => a + includedOf(p, cart[p.code].userCount, 'lump'), 0);
  const monthlyTotal = selected
    .filter((p) => cart[p.code].paymentMethod === 'monthly12')
    .reduce((a, p) => a + includedOf(p, cart[p.code].userCount, 'monthly12'), 0);

  const amountOf = (p: Product) => {
    const l = cart[p.code];
    if (!l?.checked) return null;
    return includedOf(p, l.userCount, l.paymentMethod);
  };

  const next = () => {
    const st = loadState();
    saveState({ ...st, cart });
    nav('/purchase/account');
  };

  return (
    <PurchaseLayout step={1} sectionTitle="プランの選択" sectionSub="ご希望のプランをご選択ください">
      <div className="pf-cards">
        {products.map((p) => {
          const l = cart[p.code];
          if (!l) return null;
          const isNavi = p.paymentTerms === 'A_or_B';
          const unitLabel = p.unit === 'per_user_month' ? '1名・月' : '1名・回';
          return (
            <div key={p.code} className={`pf-card ${l.checked ? 'sel' : ''}`}>
              <div className="chk" onClick={() => up(p.code, { checked: !l.checked })}>✓</div>
              <div className="logo">GB<span>{p.name.replace('GB', '')}</span></div>
              <div className="price">
                <b>¥{p.unitPrice.toLocaleString()}</b> <small>/ {unitLabel}（税抜）</small>
              </div>
              <div className="feats">
                {PLAN_FEATURES[p.code] ? (
                  <>
                    <div className="feats-h">{PLAN_FEATURES[p.code].title}</div>
                    <ul className="feats-list">
                      {PLAN_FEATURES[p.code].items.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
              {isNavi && (
                <div className="pay">
                  <div className="lb">お支払い方法</div>
                  <div className="pf-toggle">
                    <button className={l.paymentMethod === 'lump' ? 'on' : ''} onClick={() => up(p.code, { paymentMethod: 'lump' })}>年払い</button>
                    <button className={l.paymentMethod === 'monthly12' ? 'on' : ''} onClick={() => up(p.code, { paymentMethod: 'monthly12' })}>月払い</button>
                  </div>
                </div>
              )}
              <div className="cnt">
                <div className="lb">利用人数を入力</div>
                <div className="cnt-row">
                  <input type="text" inputMode="numeric" value={l.userCount}
                    onChange={(e) => up(p.code, { userCount: Math.max(1, parseInt(e.target.value.replace(/[^0-9]/g, '') || '1', 10)) })} />
                  <button className="step-b" aria-label="増やす" onClick={() => up(p.code, { userCount: l.userCount + 1 })}>+</button>
                  <button className="step-b" aria-label="減らす" onClick={() => up(p.code, { userCount: Math.max(1, l.userCount - 1) })}>−</button>
                </div>
                {l.checked && (
                  <div className="sub-total">
                    小計：<b>{isNavi
                      ? (l.paymentMethod === 'monthly12'
                          ? `${yen(includedOf(p, l.userCount, 'monthly12'))} / 月`
                          : `${yen(includedOf(p, l.userCount, l.paymentMethod))} / 年`)
                      : `${yen(includedOf(p, l.userCount, l.paymentMethod))} / 回`}</b>（税込）
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 下部 合計バー */}
      <div className="pf-bar">
        <div className="pf-bar-in">
          {products.map((p) => (
            <div className="li" key={p.code}>
              {p.name}
              <b>{amountOf(p) == null ? <span className="none">未選択</span> :
                cart[p.code].paymentMethod === 'monthly12' ? `${yen(amountOf(p)!)}/月` : yen(amountOf(p)!)}</b>
            </div>
          ))}
          <div className="total">
            <div className="lb">お支払い金額（税込）</div>
            <div className="v">
              {lumpTotal === 0 && monthlyTotal === 0
                ? yen(0)
                : (
                  <>
                    {monthlyTotal > 0 && `毎月 ${yen(monthlyTotal)}`}
                    {monthlyTotal > 0 && lumpTotal > 0 && ' ＋ '}
                    {lumpTotal > 0 && `一括 ${yen(lumpTotal)}`}
                  </>
                )}
            </div>
          </div>
          <button className="pf-next" disabled={selected.length === 0} onClick={next}>契約情報入力へ</button>
        </div>
      </div>
    </PurchaseLayout>
  );
};

export default PurchasePlanPage;
